/**
 * A layer over interacting with window.location that allows overriding the
 * global location temporarily for testing.
 */
import * as Domains from './domains';
import { ConfigUrl, UrlParts } from './index';
import * as Url from './url';

function locationScheme(location: Location) {
  let protocol = location?.protocol;
  if (!protocol) {
    return undefined;
  }
  if (protocol.endsWith(':')) {
    // the URL protocol includes a colon, which shouldn't be returned
    protocol = protocol.substr(0, protocol.length - 1);
  }
  return protocol;
}

export function checkedLocation(location?: Location): Location {
  if (!location) {
    throw new Error('Missing location.');
  }
  return location;
}

function defaultUrlPartsFromLocation(location: Location, urlParts: UrlParts) {
  // only includes the parts of the url that are likely to actually be useful in the
  // target url
  if (!urlParts.scheme) {
    urlParts.scheme = locationScheme(location);
  }
  if (!urlParts.host) {
    urlParts.host = location.hostname;
  }
  return urlParts;
}

/**
 * Create a url (a string) from the passed `configUrl`.
 *
 * If the configUrl is a string, it is returned verbatim.
 *
 * If the configUrl is an object of url parts, the parts will be assembled into
 * a url. If one of the required parts is missing and cannot be defaulted, an
 * error will be thrown.
 *
 * @param location
 * @param configUrl
 */
export function buildOptionalUrl(location: Location, configUrl?: ConfigUrl | null) {
  if (!configUrl) {
    return undefined;
  }
  if (typeof configUrl === 'string') {
    return configUrl;
  }
  configUrl = defaultUrlPartsFromLocation(location, configUrl);
  return Url.urlPartsToString(configUrl);
}

export function buildUrl(location: Location, configUrl: ConfigUrl) {
  const url = buildOptionalUrl(location, configUrl);
  if (!url) {
    throw new Error('Could not build required url.');
  }
  return url;
}

/**
 * Returns true if the current location is localhost, false otherwise.
 *
 * @param location The location to check.
 */
export function isLocalhost(location: Location) {
  return Domains.isLocalhost(location.hostname);
}

/**
 * Returns the value of a query param in the current location.
 *
 * @param location The Location to check.
 * @param name The name of the query param to return.
 */
export function queryParamValue(location: Location, name: string): string | undefined {
  if (!location.href) {
    return undefined;
  }
  const searchParams = new URL(location.href).searchParams;
  return searchParams.get(name) ?? undefined;
}

export function windowLocation(): Location | undefined {
  // typescript will complain window is undefined unless explicitly checked first
  if (typeof window === 'undefined') {
    return undefined;
  }
  return window.location;
}
